import type { DscWorkbenchDashboardResponse } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { dscWorkbenchApi } from "../../../api/client";
import { dscWorkbenchKeys } from "./dscWorkbenchKeys";

/**
 * Shared queryOptions for a deal's DSC Workbench dashboard (key + queryFn).
 * Backed by the generated WorkbenchDashboardApi.getWorkbench, which returns
 * every Step 6 workbench section in one composite response (driver,
 * secondSigner, vehicle, financials, …). Partial failures degrade gracefully —
 * failed sections are omitted and surfaced as warnings in `metadata`, so
 * consumers should read each section defensively rather than assume the whole
 * payload succeeded.
 *
 * Reusable by useQuery, prefetchQuery, ensureQueryData, getQueryData, and
 * setQueryData.
 * @param {string} approvalId - The deal / approval identifier.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function dscWorkbenchQuery(approvalId: string) {
  return queryOptions({
    queryKey: dscWorkbenchKeys.detail({ approvalId }),
    queryFn: async ({ signal }): Promise<DscWorkbenchDashboardResponse> => {
      const response = await dscWorkbenchApi.getWorkbench(approvalId, { signal });
      return response.data;
    },
    staleTime: 30_000,
    enabled: !!approvalId,
  });
}

/**
 * Reads the DSC Workbench dashboard for a deal. Disabled (never fires) until
 * the approval id is present, so callers can render an empty/loading state
 * instead of issuing a doomed `/approvals//…` request.
 * @param {string} approvalId - The deal / approval identifier.
 * @returns {ReturnType<typeof useQuery<DscWorkbenchDashboardResponse>>} The TanStack Query result.
 */
export function useDscWorkbench(approvalId: string) {
  return useQuery(dscWorkbenchQuery(approvalId));
}
